import moment from 'moment';
import { ACCOUNT_USAGE_V2_URL } from 'api/BillingApi';
import { JVT_COUNT } from 'modules/billing/constants';
import { pickMetric } from 'modules/billing/contexts/BillingQueryContext.utils';
import { Metric, Project } from 'modules/billing/types';
import { convertDateToPST } from 'modules/billing/utils';
import { isJourneyMetric, isOrgMetric, isProjectMetric } from 'modules/billing/utils/matchers';
import { useEffect, useMemo } from 'react';
import { ApiMethod } from 'services/api';
import { useRestMutation, useRestQuery } from 'utils/hooks/api';
import { gql } from '@apollo/client';
import { useGraphql } from 'utils/hooks/useGraphql';

// all customers get a free 25M JVT events, that also gets added on top of their contract purchases
export const FREE_JVT_ORG_LIMIT = 25 * 1000000;

type OrgUsageLimitResults = {
  //annual limit is the limit in customer's contract
  annualLimit: number;
  // combined limit is the annual limit + free limit
  combinedLimit: number;
  asOfDatePST: string;
  termStartDatePST: string;
  termEndDatePST: string;
  attributes: {};
};

const getDateInPST = (input: Date) => moment(convertDateToPST(moment(input))).toDate();

export const useGetOrgUsageLimit = (organizationId: number) => {
  const { mutate, data, isLoading } = useRestMutation(`/i/account/usageLimits`, ApiMethod.post);
  useEffect(
    () =>
      mutate({
        organizationId,
        metricNames: [JVT_COUNT],
      }),
    [organizationId],
  );

  const metricUsageLimit = useMemo(() => {
    if (
      data &&
      data.metricUsageLimit &&
      data.metricUsageLimit.JvtCount &&
      data.metricUsageLimit.JvtCount.length > 0
    ) {
      return {
        ...data.metricUsageLimit.JvtCount[0],
        combinedLimit: (data.metricUsageLimit.JvtCount[0].annualLimit || 0) + FREE_JVT_ORG_LIMIT,
      } as OrgUsageLimitResults;
    } else {
      return {
        combinedLimit: FREE_JVT_ORG_LIMIT,
      } as OrgUsageLimitResults;
    }
  }, [data]);

  return {
    data,
    isLoading,
    metricUsageLimit,
  };
};

const useGetJvtUsage = (startDateRaw: Date, endDateRaw: Date) => {
  /* Since the date time picker in the billing page always forces PST, we need to match it */
  const startDate = getDateInPST(startDateRaw);
  const endDate = getDateInPST(endDateRaw);

  const { data, isError, isLoading } = useRestQuery(
    ACCOUNT_USAGE_V2_URL,
    {
      startDateTime: startDate,
      endDateTime: endDate,
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const metrics = useMemo(() => {
    if (data && data.metrics) {
      return data.metrics as Metric[];
    }
    return [];
  }, [data]);

  const orgMetricsByMetricName = useMemo(() => {
    return getOrgMetricsByMetricName(metrics);
  }, [data]);

  const totalJvtCountPerOrg = getTotalJvtCountValuePerOrg(orgMetricsByMetricName);

  const jvtCountPerProject = getJvtCountPerProject(metrics);

  // this is unused for now, might re-enable in the future
  //const jvtCountPerProjectPerJourney = getJvtCountPerProjectPerJourney(metrics);

  const displayStartDate = formatDisplayDate(startDate);
  const displayEndDate = formatDisplayDate(endDate);

  const displayDateRange = `${displayStartDate} - ${displayEndDate}`;

  return {
    metrics,
    isError,
    isLoading,
    totalJvtCountPerOrg,
    jvtCountPerProject,
    displayDateRange,
  };
};

export const useGetYearlyJvtUsage = () => {
  const currentDate = new Date();
  const startDate = new Date(currentDate.getFullYear(), 0, 1);
  const endDate = new Date(currentDate.getFullYear(), 11, 31);
  return useGetJvtUsage(startDate, endDate);
};

export const useGetMonthlyJvtUsage = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  return useGetJvtUsage(startDate, endDate);
};

const formatDisplayDate = (d: Date) =>
  d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/* this is copied from BillingQueryContext helper function
   a follow up ticket will be created to consolidate/refactor it.
 */
const getOrgMetricsByMetricName = (metrics: Metric[]) => {
  const orgMetrics = metrics?.filter(isOrgMetric);
  return orgMetrics.reduce<Record<string, Metric | Metric[]>>((acc, orgMetric) => {
    if (!acc[orgMetric.metricName]) {
      acc[orgMetric.metricName] = orgMetric;
    } else if (Array.isArray(acc[orgMetric.metricName])) {
      /** If there are 3 or more metrics with the same metricName */
      acc[orgMetric.metricName] = [...(acc[orgMetric.metricName] as Metric[]), orgMetric];
    } else {
      /**
       * If there are 2 metrics with the same metricName -- they are grouped
       * so that they can be displayed under a collapsible grouping.
       */
      acc[orgMetric.metricName] = [acc[orgMetric.metricName] as Metric, orgMetric];
    }
    return acc;
  }, {});
};

const getTotalJvtCountValuePerOrg = (orgMetricsByMetricName: Record<string, Metric | Metric[]>) => {
  return pickMetric(orgMetricsByMetricName)(JVT_COUNT)?.value || 0;
};

const getProjectIdsAndProjectMetricsByProjectIdByMetricName = (projectMetrics: Metric[]) => {
  const projectMetricsByProjectId = projectMetrics.reduce(
    (acc, metric) => {
      const { id: projectId } = metric?.project as Project;
      if (!acc[projectId]) {
        acc[projectId] = [metric];
      } else {
        acc[projectId] = [...acc[projectId], metric];
      }
      return acc;
    },
    {} as Record<number, Metric[]>,
  );

  const projectIds = Object.keys(projectMetricsByProjectId).map(Number);

  const projectMetricsByProjectIdByMetricName = projectIds.reduce(
    (byId, projectId) => {
      const metricsByMetricName = projectMetricsByProjectId[projectId].reduce(
        (byName, metric) => {
          const current = byName[metric.metricName];
          if (byName[metric.metricName]) {
            const next = Array.isArray(current) ? [...current, metric] : [current, metric];
            return { ...byName, [metric.metricName]: next };
          } else {
            return { ...byName, [metric.metricName]: metric };
          }
        },
        {} as Record<string, Metric | Metric[]>,
      );
      return { ...byId, [projectId]: metricsByMetricName };
    },
    {} as Record<number, Record<string, Metric | Metric[]>>,
  );

  return {
    projectIds,
    projectMetricsByProjectIdByMetricName,
  };
};

const getJvtCountPerProject = (metrics: Metric[]) => {
  // have to filter out journey metrics in order to not have duplicates
  const projectMetrics = metrics?.filter(isProjectMetric)?.filter((m) => !isJourneyMetric(m));

  const { projectIds, projectMetricsByProjectIdByMetricName } =
    getProjectIdsAndProjectMetricsByProjectIdByMetricName(projectMetrics);

  const jvtCountPerProject = projectIds.map((pId) => {
    const metric = pickMetric(projectMetricsByProjectIdByMetricName[pId])(JVT_COUNT);
    const projectName = (metric as any)?.project?.name;
    return {
      id: pId,
      name: projectName,
      value: metric.value,
    };
  });

  jvtCountPerProject.sort((a, b) => a.value - b.value);

  return jvtCountPerProject;
};

export const getJvtCountPerProjectPerJourney = (metrics: Metric[]) => {
  const projectMetrics = metrics?.filter(isJourneyMetric);
  const { projectIds, projectMetricsByProjectIdByMetricName } =
    getProjectIdsAndProjectMetricsByProjectIdByMetricName(projectMetrics);

  const jvtCountPerProjectPerJourney = projectIds.map((pId) => {
    const metricsByJourneyIds = projectMetricsByProjectIdByMetricName[pId][JVT_COUNT];
    const metricsByProjectIdByJourneyId = Array.isArray(metricsByJourneyIds)
      ? metricsByJourneyIds
      : [metricsByJourneyIds];
    const projectName =
      metricsByProjectIdByJourneyId.length > 0
        ? metricsByProjectIdByJourneyId[0]?.project?.name
        : undefined;
    const countByJourneyId = metricsByProjectIdByJourneyId.map((m) => {
      const journeyId = (m as any)?.journeyId;
      return {
        journeyId: journeyId as string,
        value: m.value,
      };
    });
    return {
      projectId: pId,
      projectName,
      journeys: countByJourneyId,
    };
  });

  return jvtCountPerProjectPerJourney;
};

export const useGetMonthlyLimitsWithRollover = () => {
  const query = gql`
  {
    getJvtUsageAndLimit {
      limit
    }
  }
  `;
  const { data, errorMessage, isLoading, error } = useGraphql(query);

  const monthlyLimit = (data?.getJvtUsageAndLimit?.limit ?? 1) as number;

  return {
    monthlyLimit,
    errorMessage,
    isLoading,
    error,
  };
};
